import { NgIf, NgFor, CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Component, input } from '@angular/core';
import { QuoteSummaryService } from '../../services/quote.summary.service';
import { QuoteSummaryDTO, RfqPartDTO, PartQuantityDTO } from './quote.summary.dto';
import { MixPanelEvents, QuoteStatus, TrackingEvents, TrackingModule } from '../shared/enums';
import { ActivatedRoute, Router, NavigationExtras } from '@angular/router';
import { QuoteBaseComponent } from '../quote.base.component';
import { ToastrService } from 'ngx-toastr';
import { OnlyPositiveNumbersDirective } from '../shared/only.positive.numbers.directive';
import { FormatDecimalDirective } from '../directives/format.decimal.directive';
import { FormatNumberPipe } from '../pipes/format.number.pipe';
import { SummaryData } from './quote.summary.model';
import { Rfq, RfqPart } from '../quote-assist/rfq.model';
import { QuoteAssistService } from '../../services/quote.assist.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'quote-summary',
  templateUrl: './quote-summary.component.html',
  imports: [
    NgIf, 
    NgFor, 
    CommonModule, 
    FormsModule, 
    FormatDecimalDirective, 
    OnlyPositiveNumbersDirective, 
    FormatNumberPipe
  ],
  styleUrl: './quote-summary.component.scss'
})

export class QuoteSummaryComponent extends QuoteBaseComponent {
  enableDraftButton = false;
  queryParams = {
    rfqId: 0,
    companyId: 0,
    rfqPartId: 0,
    contactId:0,
    isTrackingEnabled:'false',
    integration: ''
  }
  assistData: any;
  assistPart!: RfqPart;
  quoteExpiryMinDate: string;
  currentPart: RfqPartDTO | null = null;
  nextPart: RfqPartDTO | null = null;
  quoteSummaryData: QuoteSummaryDTO = {
    rfqId: 0,
    rfqName: '',
    companyId: 0,
    supplierId: '',
    rfqPartList: [],
    buyerAddress: '',
    buyerEmail: '',
    supplierMetaData: null,
    data: {},
    quoteNumber: '',
    paymentTerm: '',
    isAllPartCompleted: false,
    quoteExpiryDate: '',
    doesAllPartCompleted: (rfqPartList: RfqPartDTO[]): boolean => false
  }
  showReviewQuoteData: boolean = false;
  skippedCaseAmountField = ["tooling", "pricePerUnit", "miscellaneous", "shippingHandling"];
  private subscription: Subscription = new Subscription();
  constructor(
    private quoteAssistService: QuoteAssistService,
    private quoteSummaryService: QuoteSummaryService,
    private toastrService: ToastrService,
    private activatedRoute: ActivatedRoute,
    private router: Router
  ) {
    super();
    const today = new Date();
    this.quoteExpiryMinDate = today.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  }

  ngOnInit() {
   this.subscription.add(
    this.quoteSummaryService.continueQuoting$.subscribe(data => {
        if (data) {  // Only proceed if data exists
            this.createQuoteHandler(data);
        }
    })
);
    this.activatedRoute.queryParams.subscribe(params => {
      if (params['rfqId']) {
        this.queryParams.rfqId = Number(params['rfqId']);
        this.queryParams.companyId = Number(params['companyId']);
        this.queryParams.rfqPartId = Number(params['rfqPartId']);
        this.queryParams.contactId = Number(params['contactId']);
        this.queryParams.isTrackingEnabled = params['isTrackingEnabled'];
        this.queryParams.integration = params['integration'];
        this.isTrackingEnabled=this.queryParams.isTrackingEnabled ;
      }

      if (this.queryParams.rfqId > 0) {
        this.quoteSummaryService.loadQuoteSummaryData(this.queryParams).subscribe({
          next: (response: any | null) => {
            if (response == null) {
              this.redirectToAssistPage();
              return;
            }
            if (response.assistData) {
              this.assistData = response.assistData;
            }
            if (response.rfqDetail) {
              this.quoteSummaryData = response.rfqDetail;
            }
            this.quoteSummaryData.paymentTerm = "";
            this.initializeQuoteSummary(this.quoteSummaryData);
          }
        });
      } else {
        this.saveAndRedirectAssistPage();
      }
    });
  }

  initializeQuoteSummary(quoteSummary: QuoteSummaryDTO | null | undefined): void {
    if (quoteSummary == null) {
      return;
    }
    let setNextPartVisible: boolean = true;
    if (this.assistData) {
      this.quoteSummaryData.quoteNumber = this.assistData.quoteNumber;
      this.quoteSummaryData.quoteExpiryDate = this.assistData.quoteExpiryDate;
      this.quoteSummaryData.paymentTerm = this.assistData.paymentTerm;
    }
    const rfqPartList = quoteSummary.rfqPartList;
    if (rfqPartList && Array.isArray(rfqPartList)) {
      for (const rfqPart of rfqPartList) {
        if (rfqPart.isPartCompleted) {
          this.enableDraftButton = true;
        }        
        const partQuantity = rfqPart?.partQuantity;
        if (partQuantity && Array.isArray(partQuantity)) {
          for (const partQty of partQuantity) {
            if (partQty != null) {
              partQty.doCalculation = false;
              partQty.qtyCompleted = partQty.summaryData.isCompleted;
              if (partQty.qtyCompleted) {
                this.confirmQuantity(partQty, rfqPart, false);
                partQty.doCalculation = true;
              }
            }
          }
        } else {
            return;
        }
      }
    } else {
      // console.warn("rfqPartList is not an array or is undefined/null in QuoteSummaryDTO");
    }
  }

  downloadCSVFile() {
    this.toastrService.warning("We are working hard to complete this feature as soon as possible");
  }

  partSelectionChange(part: RfqPartDTO) {
    if ((part.isPartCompleted && this.quoteSummaryData) || part.isAssistSkipped) {
      this.quoteSummaryData.rfqPartList.forEach((existingPart: RfqPartDTO) => {
        existingPart.isVisible = (part.rfqPartId == existingPart.rfqPartId);
      });
    }
  }

  showNextPartName(): RfqPartDTO | null {
    this.nextPart = null;
    let isCurrentPartCompleted = false;
    if (this.quoteSummaryData)
      for (const part of this.quoteSummaryData.rfqPartList) {
        if (part.isVisible) {
          this.currentPart = part;
          isCurrentPartCompleted = part.isPartCompleted;

        }
        if (!part.isPartCompleted) {
          if (isCurrentPartCompleted) {
            this.nextPart = part;
            break; // Exit the loop as soon as the condition is met
          }
        }
      }
    return this.nextPart;
  }

  saveAndRedirectToAssistPage(part: RfqPartDTO) {
    this.createQuoteHandler(false, true);
    var useAssistPage: boolean = false;
    for(const part of this.quoteSummaryData.rfqPartList){
      if(!part.isPartCompleted && !part.isAssistSkipped){
        useAssistPage = true;
        break;
      }
      else if(!part.isPartCompleted && part.isAssistSkipped){
        break;
      }
    }
    if (useAssistPage) {
      this.saveAndRedirectAssistPage();
    } else {
      const rfqPartList = this.quoteSummaryData.rfqPartList;
      if (rfqPartList && Array.isArray(rfqPartList)) {
        for (const rfqPart of rfqPartList) {
          this.partSelectionChange(rfqPart);
        }
      } else {
        // console.warn("rfqPartList is not an array or is undefined/null in QuoteSummaryDTO");
      }
    }
  }

  saveAndRedirectAssistPage() {
    this.showPageDataLoading = true
    this.assistData.quoteNumber = this.quoteSummaryData.quoteNumber;
    this.assistData.quoteExpiryDate = this.quoteSummaryData.quoteExpiryDate;
    this.assistData.paymentTerm = this.quoteSummaryData.paymentTerm;

    this.saveExistingData()?.subscribe(result => {
      this.redirectToAssistPage();
    });
  }

  redirectToAssistPage() {
    if (this.nextPart != null) {
      this.queryParams.rfqPartId = this.nextPart.rfqPartId;
    }
    const navigationExtras: NavigationExtras = {
      queryParams: this.queryParams
    };
    this.router.navigate(['/assist'], navigationExtras);
  }
  quantitySelectionChanged(part: RfqPartDTO, quantityObj: PartQuantityDTO) {
    if (!quantityObj.qtyCompleted) {
      this.toastrService.error("Please complete all required value.");
      return;
    }
    part.partQuantity.forEach((existingQuantityObj: PartQuantityDTO) => {
      existingQuantityObj.isVisible = quantityObj.order === existingQuantityObj.order;
    });
  }

  confirmQuantity(quantityObj: PartQuantityDTO, part: RfqPartDTO, saveData: boolean = true) {

    if (this.isNullOrBlankOrZero(quantityObj.summaryData.pricePerUnit.totalPrice) && saveData) {
      this.toastrService.error("Price Per Unit is required");
      return;
    };
    if (this.isNullOrBlankOrZero(quantityObj.summaryData.leadTime) && saveData) {
      this.toastrService.error("Lead time is required.");
      return;
    }
    //TODO other case price ca
    quantityObj.qtyCompleted = true;
    quantityObj.summaryData.isCompleted = true;
    quantityObj.isVisible = false;
    part.partQuantity.forEach((existingQuantityObj: PartQuantityDTO) => {
      if (quantityObj.order + 1 === existingQuantityObj.order) {
        existingQuantityObj.isVisible = true;
      }
    });
    this.updateAssistSummaryData(quantityObj, part, false);
    part.isPartCompleted = part.doesPartCompleted(part.partQuantity);
    if (part.isPartCompleted) {
      this.enableDraftButton = true;
    }
    if (saveData) {
      this.saveExistingData()?.subscribe(result => { });
    }
    if (this.quoteSummaryData)
      if (this.quoteSummaryData.doesAllPartCompleted(this.quoteSummaryData.rfqPartList)) {
        this.quoteSummaryData.isAllPartCompleted = true;
      }
  }


  saveExistingData() {
    
    if (!this.assistData) {
      return;
    }
    let quoteSummaryData: Rfq = JSON.parse(JSON.stringify(this.assistData));
    return this.quoteSummaryService.updateExistingData(quoteSummaryData);
  }

  reviewQuoteSummary() {
    // this.pushTrackingDataToMFG("supplier quote assist review quote");
    this.addTracking(this.quoteAssistService, TrackingModule.QuoteSummary, TrackingEvents.ReviewQuote, this.assistPart, this.assistData,
      MixPanelEvents.ReviewQuote, this.queryParams);
    this.saveExistingData()?.subscribe(result => {
      this.showReviewQuoteData = true
    })
  }

  updateAssistSummaryData(quantityObj: PartQuantityDTO, inputPart: RfqPartDTO, trackEnabled: boolean = true) {
    if (this.assistData?.rfqPartList) {
      // let assistPart:RfqPart = this.assistData.rfqPartList.find((part:RfqPart) => part.rfqPartId == inputPart.rfqPartId);
      this.assistPart = this.assistData.rfqPartList.find((part: RfqPart) => part.rfqPartId == inputPart.rfqPartId);
      if (this.assistPart) {
        let assistQty = this.assistPart.partQuantity.find(qty => qty.order == quantityObj.order);
        if (assistQty) {
          assistQty.summaryData = quantityObj.summaryData;
          // Handle blank or null values for specific total costs
          if (!assistQty.summaryData.shippingHandling?.totalCost) {
            assistQty.summaryData.shippingHandling.totalCost = 0;
          }
          if (!assistQty.summaryData.miscellaneous?.totalCost) {
            assistQty.summaryData.miscellaneous.totalCost = 0;
          }
          if (!assistQty.summaryData.tooling?.totalCost) {
            assistQty.summaryData.tooling.totalCost = 0;
          }
        }

        if (trackEnabled) {
          this.addTracking(this.quoteAssistService, TrackingModule.QuoteSummary, TrackingEvents.SummaryStarted, this.assistPart, this.assistData,
            MixPanelEvents.SummaryStarted, this.queryParams);
        }
      }
    }
  }

  calculateTotalToolingCost(quantityObj: PartQuantityDTO) {
    let totalCost = 0.0;
    let itemList = quantityObj.summaryData.tooling.toolingList
    if (itemList) {
      itemList.forEach((tooling: any) => {
        totalCost = totalCost + this.convertInternationalToDecimal(this.formatNumbersToFixed2(tooling.estimatedCost))
      });
    }
    return totalCost;
  }

  getTotalCostForGivenQuantity(qtyObj: PartQuantityDTO) {
    let totalCost = 0.0;
    totalCost = totalCost + this.calculateTotalToolingCost(qtyObj);
    return totalCost;
  }

  getSkippedAssistQuantityTotalCost(quantityObj: PartQuantityDTO) {
    quantityObj.summaryData = this.formatNumbersToFixed2(quantityObj.summaryData);
    let totalCost = 0.0;

    this.skippedCaseAmountField.forEach(key => {
      if (key === 'pricePerUnit') {
        const pricePerUnit = quantityObj.summaryData.pricePerUnit;
        totalCost += (this.convertInternationalToDecimal(pricePerUnit.totalPrice) || 0) * quantityObj.qty;
      } else {
        const amount = quantityObj.summaryData[key as keyof SummaryData] as any;
        totalCost += this.convertInternationalToDecimal(amount.totalCost) || 0;
      }
    });
    quantityObj.totalCost = totalCost;
    return totalCost;
  }

  calculateToolingTotal(quantityObj: PartQuantityDTO) {
    let totalCost = this.convertInternationalToDecimal((quantityObj.summaryData.tooling?.toolingList || []).reduce(
      (sum: number, tool: any) => sum + this.convertInternationalToDecimal(tool.estimatedCost.toString()),
      0
    ));
    quantityObj.summaryData.tooling.cost = this.formatNumbersToFixed2(totalCost);
    quantityObj.summaryData.tooling.profitValue = this.convertInternationalToDecimal((totalCost * ((this.convertInternationalToDecimal(quantityObj.summaryData.tooling.profitMargin) / 100))))
    return this.convertInternationalToDecimal((totalCost + quantityObj.summaryData.tooling.profitValue).toFixed(2))

  }

  calculateShippingTotal(quantityObj: PartQuantityDTO) {
    let totalCost = this.convertInternationalToDecimal(quantityObj.summaryData.shippingHandling.cost);
    quantityObj.summaryData.shippingHandling.cost = this.formatNumbersToFixed2(totalCost);
    quantityObj.summaryData.shippingHandling.profitValue = this.convertInternationalToDecimal((totalCost * ((this.convertInternationalToDecimal(quantityObj.summaryData.shippingHandling.profitMargin) / 100))))
    return this.convertInternationalToDecimal((totalCost + quantityObj.summaryData.shippingHandling.profitValue).toFixed(2))

  }

  calculatePricePerUnitCost(quantityObj: PartQuantityDTO) {
    const { materials, machining, labor, additionalManufacturingRequirements, overhead } = quantityObj.summaryData.pricePerUnit;
    const materialsCost = this.convertInternationalToDecimal(materials.totalCost);
    const machiningCost = this.convertInternationalToDecimal(machining.totalCost)
    const laborCost = this.convertInternationalToDecimal(labor.totalCost);
    const amrCost = this.convertInternationalToDecimal(additionalManufacturingRequirements.totalCost);
    const overheadCost = this.convertInternationalToDecimal(overhead.totalCost);
    let pricePerUnitValue = (
      (materialsCost) +
      (machiningCost) +
      laborCost +
      amrCost +
      overheadCost
    );
    quantityObj.summaryData.pricePerUnit.profitValue = this.parseAndFormat(this.parseAndFormat(pricePerUnitValue) * (this.parseAndFormat(quantityObj.summaryData.pricePerUnit.profitMargin) / 100));
    // quantityObj.summaryData.pricePerUnit.profitValue = this.convertInternationalToDecimal((((this.formatNumbersToFixed2(pricePerUnitValue)) * ((this.convertInternationalToDecimal(this.formatNumbersToFixed2(quantityObj.summaryData.pricePerUnit.profitMargin)) / 100)))).toFixed(2));
  }

  calculateMiscellaneousCost(quantityObj: PartQuantityDTO) {
    const { miscellaneous } = quantityObj.summaryData;
    // const qty = this.tryParseInt(quantityObj.qty);
    const miscellaneousCost = this.convertInternationalToDecimal(miscellaneous.itemList.reduce(
      (acc, otherCost) => {
        return acc + this.convertInternationalToDecimal(otherCost.estimatedCost)
      }, 0
    ));
    quantityObj.summaryData.miscellaneous.profitValue = this.parseAndFormat(this.parseAndFormat(miscellaneousCost) * (this.parseAndFormat(quantityObj.summaryData.miscellaneous.profitMargin) / 100));
    // quantityObj.summaryData.miscellaneous.profitValue = this.convertInternationalToDecimal(this.convertInternationalToDecimal((this.formatNumbersToFixed2(miscellaneousCost)) * ((this.convertInternationalToDecimal(this.formatNumbersToFixed2(quantityObj.summaryData.miscellaneous.profitMargin)) / 100))).toFixed(2));
  }

  calculateTotalCost(quantityObj: PartQuantityDTO): number {
    const { summaryData } = quantityObj;

    const { materials, machining, labor, additionalManufacturingRequirements, overhead } = summaryData.pricePerUnit;
    const { tooling, miscellaneous, shippingHandling, pricePerUnit } = summaryData;

    const qty = this.tryParseInt(quantityObj.qty);
    const materialsCost = quantityObj.doCalculation ? this.convertInternationalToDecimal(materials.totalCost) : materials.unitCost * qty;
    const machiningCost = quantityObj.doCalculation ? this.convertInternationalToDecimal(machining.totalCost) : this.convertInternationalToDecimal(machining.machineList.reduce(
      (acc, item) => acc + this.convertInternationalToDecimal(item.costPerMin) * this.convertInternationalToDecimal(item.cyclePerUnit), 0
    ) * qty);
    const laborCost = this.convertInternationalToDecimal(labor.totalCost);
    const amrCost = this.convertInternationalToDecimal(additionalManufacturingRequirements.totalCost);
    const overheadCost = this.convertInternationalToDecimal(overhead.totalCost);

    const toolingCost = this.convertInternationalToDecimal(tooling.cost);
    const miscellaneousCost = this.convertInternationalToDecimal(miscellaneous.itemList.reduce(
      (acc, otherCost) => {
        return acc + this.convertInternationalToDecimal(otherCost.estimatedCost)
      }, 0
    ));
    const shippingHandlingCost = this.convertInternationalToDecimal(shippingHandling.cost);
    materials.totalCost = this.convertInternationalToDecimal(materialsCost);
    machining.totalCost = this.convertInternationalToDecimal(machiningCost);
    // Calculate price per unit
    let pricePerUnitValue = this.convertInternationalToDecimal(((
      (materialsCost) +
      (machiningCost) +
      laborCost +
      amrCost +
      overheadCost
    )));

    if (!quantityObj.doCalculation)
      tooling.profitValue = this.parseAndFormat((toolingCost * ((this.convertInternationalToDecimal(summaryData.tooling.profitMargin) / 100))));
    tooling.totalCost = this.parseAndFormat((toolingCost + this.convertInternationalToDecimal(tooling.profitValue)));

    if (!quantityObj.doCalculation)
      pricePerUnit.profitValue = this.parseAndFormat((((pricePerUnitValue) * ((this.convertInternationalToDecimal(summaryData.pricePerUnit.profitMargin) / 100)))));
    pricePerUnit.totalPrice = this.parseAndFormat((((pricePerUnitValue) + this.convertInternationalToDecimal(pricePerUnit.profitValue)) / qty));

    if (!quantityObj.doCalculation)
      miscellaneous.profitValue = this.parseAndFormat(this.convertInternationalToDecimal((miscellaneousCost) * ((this.convertInternationalToDecimal(summaryData.miscellaneous.profitMargin) / 100))));
    miscellaneous.totalCost = this.parseAndFormat(this.convertInternationalToDecimal((miscellaneousCost) + this.convertInternationalToDecimal(miscellaneous.profitValue)));

    if (!quantityObj.doCalculation)
      shippingHandling.profitValue = this.parseAndFormat(this.convertInternationalToDecimal((shippingHandlingCost) * ((this.convertInternationalToDecimal(summaryData.shippingHandling.profitMargin) / 100))));
    shippingHandling.totalCost = this.parseAndFormat(this.convertInternationalToDecimal((shippingHandlingCost) + this.convertInternationalToDecimal(shippingHandling.profitValue)));


    const totalCost =
      this.parseAndFormat((tooling.totalCost + (pricePerUnit.totalPrice * qty) + miscellaneous.totalCost + shippingHandling.totalCost));


    quantityObj.doCalculation = true;
    quantityObj.totalCost = totalCost;
    return totalCost;
  }

  createQuoteHandler(isSubmit:boolean=true, autoSaveDraft:boolean=false) {
    const quoteSummaryData = JSON.parse(JSON.stringify(this.quoteSummaryData));
    const hasInvalidLeadTime = quoteSummaryData.rfqPartList.some((part: any) => 
      part.partQuantity.some((quantity: any) => 
        !quantity.summaryData.leadTime || Number(quantity.summaryData.leadTime) === 0
      )
    );

    if (hasInvalidLeadTime) {
      this.toastrService.error("Lead Time cannot be empty or zero");
      return;
    }
    
    if(isSubmit){
      if (this.isNullOrBlankOrEmpty(quoteSummaryData.quoteNumber)) {
        this.toastrService.error("Quote Reference Number is required");
        return;
      }
      if (this.isNullOrBlankOrEmpty(quoteSummaryData.quoteExpiryDate)) {
        this.toastrService.error("Quote Expiry Date is required");
        return;
      }
    }
    this.showPageDataLoading = true
    const paymentTermObj = this.paymentTermList.filter(term => {
      return term.code === quoteSummaryData.paymentTerm;
    })[0];
    let rfqData: any = {
      rfqId: quoteSummaryData.rfqId,
      quoteExpiryDate: quoteSummaryData.quoteExpiryDate,
      quoteReferenceNumber: quoteSummaryData.quoteNumber,
      paymentTerms: paymentTermObj,
      isSubmitted:isSubmit
    };

    this.assistData.quoteNumber = this.quoteSummaryData.quoteNumber;
    this.assistData.quoteExpiryDate = this.quoteSummaryData.quoteExpiryDate;
    this.assistData.paymentTerm = this.quoteSummaryData.paymentTerm;

    // Check and replace blank/null values with 0
    if (this.assistData.rfqPartList) {
      this.assistData.rfqPartList.forEach((part: any) => {
        if (part.partQuantity) {
          part.partQuantity.forEach((qty: any) => {
            if (qty.summaryData) {
              if (!qty.summaryData.shippingHandling?.totalCost) {
                qty.summaryData.shippingHandling.totalCost = 0;
              }
              if (!qty.summaryData.miscellaneous?.totalCost) {
                qty.summaryData.miscellaneous.totalCost = 0;
              }
              if (!qty.summaryData.tooling?.totalCost) {
                qty.summaryData.tooling.totalCost = 0;
              }
            }
          });
        }
      });
    }

    const payload = {
      CompanyId: this.queryParams.companyId,
      SupplierId: this.queryParams.contactId,
      RfqId: this.queryParams.rfqId,
      QuoteStatus: isSubmit ? QuoteStatus.Quoted : QuoteStatus.Draft,
      RfqPartsMetaData: JSON.stringify(this.assistData),
      Source: this.queryParams?.integration
    };

    this.quoteSummaryService.saveDraft(payload).subscribe({
      next: (response: any) => {
        if(response.status){
            this.saveExistingData()?.subscribe(result=>{
              let rfqPartList: any = [];
              let notValid = false;
              quoteSummaryData.rfqPartList.forEach((part: any) => {
                if(part.isPartCompleted){
                let rfqPart: any = {
                  rfqPartId: part.rfqPartId,
                  rfqPartName: part.rfqPartName
                };
                
                let partQuantityList: any = [];
                part.partQuantity.forEach((quantityObj: any) => {
                  let assistedData: any = {};
                  assistedData["rfqPartId"] = part.rfqPartId;
                  assistedData["rfqPartQuantityId"] = quantityObj.rfqPartQuantityId;
                  assistedData["quantityLevel"] = quantityObj.order;
                  assistedData["perUnitPrice"] = quantityObj.summaryData.pricePerUnit.totalPrice;
                  assistedData["shippingAmount"] = quantityObj.summaryData.shippingHandling.totalCost
                  assistedData["toolingAmount"] = quantityObj.summaryData.tooling.totalCost
                  assistedData["estLeadTimeValue"] = Number(quantityObj.summaryData.leadTime);
                  assistedData["awardedQty"] = quantityObj.qty;
                  assistedData["estLeadTimeRange"] = "Days"
                  assistedData["miscellaneousAmount"] = quantityObj.summaryData.miscellaneous.totalCost;
                  if (
                    this.isNullOrBlankOrZero(assistedData["perUnitPrice"]) ||
                    this.isNullOrBlankOrZero(assistedData["estLeadTimeValue"])
                  ) {
                    notValid = true;
                  }
                  partQuantityList.push(assistedData);
                });
                rfqPart.partQuantityList = partQuantityList;
                rfqPartList.push(rfqPart);
              }
              });
              rfqData["rfqPartList"] = rfqPartList;
              if (notValid) {
                this.showPageDataLoading = false;
                this.toastrService.warning("Per Unit Price and Lead Time cannot zero");
                return;
              }
              if(rfqData["rfqPartList"].length<1){
                this.showPageDataLoading = false;
                this.toastrService.error("At least one part should be completed");
                return ;
              }

              let trackingEventName=isSubmit ? TrackingEvents.QuoteCreated : TrackingEvents.SaveToDraft;
              let mixPanelEventName=isSubmit ? MixPanelEvents.QuoteCreated : MixPanelEvents.SaveToDraft;
      
              if(!autoSaveDraft){
              this.addTracking(this.quoteAssistService, TrackingModule.QuoteAssist, 
                trackingEventName, this.assistPart, this.assistData,
                mixPanelEventName, this.queryParams);

                this.toastrService.success(isSubmit ? "Quote created successfully" : "Draft saved successfully");
                
                // Emit event to parent window and close iframe
                if (window.parent) {
                  window.parent.postMessage({
                    redirect: true,
                    isSubmitted: isSubmit
                  }, '*');
                }
              }
          });
        }
        else{
          this.toastrService.error(isSubmit ? "Quote creation failed" : "Quote save failed");
        }
        // this.showPageDataLoading = false;
      },
      error: (error: any) => {
      }
    });
  }

  pushTrackingDataToMFG(eventName: string, dataToSend: any = {}) {
    if (window.parent) {
      let dataToEmit: any = Object.assign({ isQuoteAssistTracking: true }, this.queryParams);
      dataToEmit = Object.assign(dataToEmit, dataToSend)
      dataToEmit["eventName"] = eventName
      window.parent.postMessage(dataToEmit, "*");
    }
  }
  backToAssist() {
    this.quoteSummaryData.rfqPartList.forEach((existingPart: RfqPartDTO) => {
      if(existingPart.isAssistSkipped && !existingPart.isPartCompleted){
        existingPart.isAssistSkipped = false;
        existingPart.isAssistEnabled = false;
      }
    });
    this.saveAndRedirectAssistPage();
  }
  saveDraft() {
    this.createQuoteHandler(false);
  }

}
